/* Storefront dataLayer, interactive part (page events and GTM: snippets/fk-datalayer.liquid; checkout:
 * pixels/fk-checkout-pixel.js). Pushes ee_select_item, ee_view_cart for the cart drawer, ee_add_to_cart and
 * ee_remove_from_cart for every cart change Horizon reports, and ee_add_to_wishlist / ee_remove_from_wishlist.
 * If the Analyzify app embed is still on, the notes below apply; without it silenceAnalyzify does nothing.
 * Analyzify detects cart changes from clicks on buttons it recognises, which misses Horizon's buttons and every custom
 * surface (quick add, cart upsell, bundles, wishlist, shop the look) and cannot tell which line a cart button removes.
 * The theme therefore owns these two events and switches Analyzify's click detection for them off, as well as its
 * checkout-button detection (begin_checkout is sent by the checkout pixel, pixels/fk-checkout-pixel.js).
 * The payload copies Analyzify's own events (an_analyzify-v3) so its GTM container maps them to GA4 and Ads unchanged.
 * Consent stays with GTM: tags read Consent Mode from Pandectes.
 */
(() => {
  if (window.fkDataLayer) return;
  window.fkDataLayer = true;
  window.dataLayer = window.dataLayer || [];

  const ADD = 'ee_add_to_cart';
  const REMOVE = 'ee_remove_from_cart';
  const SOURCE = 'fk-theme';
  // Free gifts are added and removed by the theme, not by the visitor.
  const IGNORED_SOURCES = ['fk-rewards'];
  let scanned = window.dataLayer.length;

  const toId = (value) => String(value === undefined || value === null ? '' : value).split('/').pop();
  const money = (cents) => Math.round(Number(cents || 0)) / 100;

  // Analyzify reads these attribute lists on every click; empty lists match nothing. begin_checkout comes from the
  // checkout pixel (pixels/fk-checkout-pixel.js): a click on the cart's checkout button is lost when the page unloads.
  function silenceAnalyzify() {
    const analyzify = window.analyzify;
    if (!analyzify) return;
    analyzify.addtocart_btn_attributes = {};
    analyzify.collection_atc_attributes = {};
    analyzify.removefromcart_btn_attributes = {};
    analyzify.checkout_btn_attributes = {};
    analyzify.accelerated_checkout_btn_attributes = {};
    analyzify.accelerated_checkout_cart_attributes = {};
  }

  // Skip our event when Analyzify still pushed its own for the same change.
  function analyzifyHandled(name) {
    const entries = window.dataLayer.slice(scanned);
    scanned = window.dataLayer.length;
    return entries.some((entry) => entry && entry.event === name && entry.ecommerce && entry.ecommerce.analyzify_source !== SOURCE);
  }

  function adsId(item) {
    const format = window.analyzify && typeof window.analyzify.adsIdFormat === 'function' ? window.analyzify.adsIdFormat : null;
    if (format) return format(item.product_id || null, item.variant_id || null, item.sku || null);
    return item.sku || String(item.variant_id);
  }

  function toItem(item, quantity, index) {
    return {
      index,
      item_id: String(item.product_id),
      item_name: item.product_title || item.title,
      item_brand: item.vendor || null,
      item_category: item.product_type || null,
      item_sku: item.sku || null,
      item_handle: item.handle || null,
      item_variant: item.variant_title || null,
      item_variant_id: String(item.variant_id),
      price: money(item.final_price === undefined ? item.price : item.final_price),
      quantity,
      id: adsId(item),
      google_business_vertical: 'retail',
    };
  }

  // Cart rows (snippets/fk-cart-line-item.liquid) carry the line's product data; a removed line is no longer in the response.
  function lineItem(key) {
    const row = [...document.querySelectorAll('tr[data-key]')].find((entry) => entry.dataset.key === key);
    if (!row || !row.dataset.fkVariantId) return null;
    const data = row.dataset;
    return {
      product_id: data.fkProductId, variant_id: data.fkVariantId, sku: data.fkSku, product_title: data.fkTitle,
      variant_title: data.fkVariant, vendor: data.fkVendor, product_type: data.fkType, handle: data.fkHandle,
      final_price: Number(data.fkPrice), quantity: Number(data.fkQuantity),
    };
  }

  function push(name, entries, result) {
    const items = entries.map(([item, quantity], index) => toItem(item, quantity, index + 1));
    if (!items.length) return;
    const total = result.cart && result.cart.cost && result.cart.cost.totalAmount;
    const currency = (total && total.currencyCode) || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active);
    const value = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: name,
      ecommerce: {
        currency,
        value: Math.round(value * 100) / 100,
        analyzify_source: SOURCE,
        implementation_type: 'extension',
        item_list_id: null,
        item_list_name: null,
        items,
      },
    });
    scanned = window.dataLayer.length;
  }

  function onAdd(event, lines) {
    const handled = analyzifyHandled(ADD);
    event.promise
      .then((result) => {
        const detail = (result && result.detail) || {};
        if (handled || detail.didError || IGNORED_SOURCES.includes(detail.source)) return;
        const cartItems = detail.items || [];
        push(ADD, lines
          .map((line) => {
            const item = cartItems.find((entry) => toId(entry.variant_id || entry.id) === toId(line.merchandiseId));
            return item ? [item, Number(line.quantity) || 1] : null;
          })
          .filter(Boolean), result);
      })
      .catch(() => {});
  }

  // Quantity changes report the new quantity. The row shows the quantity of its last render, so quick successive
  // changes start from the quantity requested last instead.
  const requested = new Map();
  function onChange(event, lines) {
    const changes = lines
      .map((line) => {
        const item = lineItem(line.id);
        if (!item) return null;
        const previous = requested.has(line.id) ? requested.get(line.id) : item.quantity;
        requested.set(line.id, Number(line.quantity));
        return [item, Number(line.quantity) - previous, line.id];
      })
      .filter((change) => change && change[1] !== 0);
    if (!changes.length) return;
    const handled = analyzifyHandled(REMOVE);
    event.promise
      .then((result) => {
        const detail = (result && result.detail) || {};
        if (detail.didError || IGNORED_SOURCES.includes(detail.source)) return;
        if (!handled) push(REMOVE, changes.filter(([, delta]) => delta < 0).map(([item, delta]) => [item, -delta]), result);
        push(ADD, changes.filter(([, delta]) => delta > 0), result);
      })
      .catch(() => changes.forEach(([, , key]) => requested.delete(key)));
  }

  document.addEventListener('shopify:cart:lines-update', (event) => {
    if (!event.promise) return;
    const lines = event.lines || [];
    if (event.action === 'add') {
      // Adds elsewhere change line quantities without going through the cart rows.
      requested.clear();
      onAdd(event, lines);
    } else if (event.action === 'remove' || event.action === 'update') onChange(event, lines);
  });

  const currency = () => (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || null;

  // A product clicked in the page's list (collection or search results, listed by snippets/fk-datalayer.liquid).
  // GA4 sends a few seconds after the push, so the product page opens before: the event is kept for that page, which
  // pushes it before ee_view_item (snippets/fk-datalayer.liquid). sessionStorage also follows a link opened in a new tab.
  document.addEventListener('click', (event) => {
    const list = window.fkDataLayerList;
    const link = event.target instanceof Element ? event.target.closest('a[href*="/products/"]') : null;
    if (!list || !Array.isArray(list.items) || !link) return;
    const handle = new URL(link.href, location.href).pathname.split('/products/')[1]?.split('/')[0];
    const item = list.items.find((entry) => entry.item_handle === handle);
    if (!item) return;
    const selectItem = {
      event: 'ee_select_item',
      ecommerce: {
        currency: currency(),
        analyzify_source: SOURCE,
        item_list_id: list.item_list_id,
        item_list_name: list.item_list_name,
        items: [item],
      },
    };
    try {
      sessionStorage.setItem('fk_select_item', JSON.stringify({ handle, payload: selectItem }));
    } catch (error) {
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push(selectItem);
    }
  }, true);

  // The cart page pushes ee_view_cart itself (snippets/fk-datalayer.liquid); the cart drawer does it when opened.
  document.addEventListener('theme-drawer:open', (event) => {
    if (!(event.target instanceof Element) || event.target.id !== 'cart-drawer') return;
    fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        if (!cart.items || !cart.items.length) return;
        const items = cart.items.map((item, index) => toItem(item, item.quantity, index + 1));
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'ee_view_cart',
          ecommerce: {
            currency: cart.currency || currency(),
            value: money(cart.total_price),
            total_item: items.length,
            total_quantity: cart.item_count,
            analyzify_source: SOURCE,
            items,
          },
        });
      })
      .catch(() => {});
  });

  // Wishlist hearts (assets/fk-wishlist.js) report through fk:track with the product handle and variant id; the
  // product data comes from the Ajax API. GA4 has add_to_wishlist; remove_from_wishlist is a custom event.
  const WISHLIST = { wishlist_item_added: 'ee_add_to_wishlist', wishlist_item_removed: 'ee_remove_from_wishlist' };
  document.addEventListener('fk:track', (event) => {
    const detail = event.detail || {};
    const name = WISHLIST[detail.event];
    const properties = detail.properties || {};
    if (!name || !properties.product_handle) return;
    fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(properties.product_handle)}.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((product) => {
        const variant = product.variants.find((entry) => entry.id === properties.variant_id) || product.variants[0];
        if (!variant) return;
        const item = toItem({
          product_id: product.id, variant_id: variant.id, sku: variant.sku, product_title: product.title,
          variant_title: product.variants.length > 1 ? variant.title : null, vendor: product.vendor,
          product_type: product.type, handle: product.handle, final_price: variant.price,
        }, 1, 1);
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: name,
          wishlist_placement: properties.placement || null,
          ecommerce: { currency: currency(), value: item.price, analyzify_source: SOURCE, items: [item] },
        });
      })
      .catch(() => {});
  });

  silenceAnalyzify();
  addEventListener('load', silenceAnalyzify, { once: true });
})();
